import React from "react";
import { GreyCard, CautionBox } from "./UI";

export default function GettingStarted() {
  return (
    <div className="sz-page-section sz-page-section-alt">
      <div className="container">
        <h2 className="sz-plain-title mb-3">Getting Started: Signup and Login Process</h2>

        <div className="sz-outer-card">
        <h4 className="fw-bold mt-2 mb-3">Signup Process</h4>
        <p>
          When starting to use Selectezi, all new users are required to first
          create an account by filling out the official Signup Form.
        </p>
        <p className="mb-4">
          This process ensures that all profiles on the platform are genuine,
          secure, and accurately categorized under a confirmed business type.
        </p>
        <p className="mb-3">
          When you visit the Signup Page, you'll be prompted to fill in the
          following details:
        </p>

        <div className="row g-3">
          <div className="col-md-6">
            <GreyCard title="Full Name">
              <p className="mb-0">
                Provide your actual name if you are an individual user, or
                your business representative's official name if registering
                on behalf of a company. This name will appear on your public
                profile and should be accurate to maintain authenticity.
              </p>
            </GreyCard>
          </div>
          <div className="col-md-6">
            <GreyCard title="Email Address">
              <p className="mb-0">
                Use a valid and original email address, this will be needed
                for verification, login, and password recovery. Ensure that
                you have full access to this email before submitting the
                form.
              </p>
            </GreyCard>
          </div>
          <div className="col-md-6">
            <GreyCard title="Mobile Number">
              <p className="mb-0">
                Provide your active mobile number, required for identity
                verification and future support communication. This helps us
                verify your account and contact you in case of any
                account-related issues.
              </p>
            </GreyCard>
          </div>
          <div className="col-md-6">
            <GreyCard title="Password">
              <p className="mb-0">
                Create a strong password with at least 8 characters,
                including one uppercase letter and one digit. A strong
                password protects your account from unauthorized access.
                Avoid using simple or common passwords.
              </p>
            </GreyCard>
          </div>
        </div>

        <h5 className="fw-bold mt-4 mb-2">Auto Verification During Signup</h5>
        <p className="mb-2">Once you submit the signup form, the system automatically verifies:</p>
        <ul className="sz-bullet-list">
          <li>If the email address is already registered.</li>
          <li>Whether all required fields are filled correctly.</li>
          <li>If the password meets security standards.</li>
          <li>Whether the selected business type is valid under platform policy.</li>
        </ul>
        <p className="mb-2">
          If any information is missing or incorrect, Selectezi will display
          real-time alerts such as:
        </p>
        <div className="sz-alert-grid-box">
          <div className="row">
            <div className="col-md-6">
              <p className="mb-1"><code>• "This email is already registered."</code></p>
              <p className="mb-0"><code>• "Please fill all required fields."</code></p>
            </div>
            <div className="col-md-6">
              <p className="mb-1"><code>• "Invalid business type selection."</code></p>
              <p className="mb-0">
                <code>
                  • "Password must contain at least 8 characters with one
                  capital letter and one number."
                </code>
              </p>
            </div>
          </div>
        </div>
        <p className="mb-4">These alerts help you fix errors before resubmitting your form.</p>

        <hr className="sz-divider mb-3" />
        <h5 className="fw-bold mb-2">Email Verification Step</h5>
        <p>
          After completing the signup form, you'll receive a verification
          email from Selectezi. To activate your account:
        </p>
        <p className="fw-bold text-primary-sz">
          Open the email and click the "Verify Account" link.
        </p>
        <CautionBox>
          Note: Without verification, you won't be able to log in or access
          your dashboard.
        </CautionBox>
        <p className="mb-2 mt-3">If you don't receive the email within a few minutes:</p>
        <ul className="sz-bullet-list mb-0">
          <li>Check your Spam or Promotions folder.</li>
          <li>Make sure your internet connection is stable.</li>
          <li>Click Resend Verification Link on the registration page if necessary.</li>
        </ul>
        </div>

        <div className="sz-outer-card">
        <h4 className="fw-bold mt-2 mb-3">Login Process</h4>
        <p>Once your account is verified, you can log in anytime by following these steps:</p>
        <ul className="sz-bullet-list">
          <li>Go to www.selectezi.com/login</li>
          <li>Enter your registered Email and Password</li>
          <li>Click Login to access your Dashboard</li>
        </ul>

        <hr className="sz-divider mb-3" />
        <h5 className="fw-bold text-primary-sz mb-2">If Login Fails</h5>
        <p className="mb-2">Try the following solutions:</p>
        <ul className="sz-bullet-list mb-0">
          <li>Click Forgot Password to reset your password via email.</li>
          <li>Ensure your email is verified before attempting login.</li>
          <li>Double check your login credentials.</li>
          <li>Confirm your internet connection is stable.</li>
          <li>
            If you still can't log in, contact Selectezi Support through the
            Helpline section (explained earlier in this guide).
          </li>
        </ul>
        </div>
      </div>
    </div>
  );
}
